/**
 * SA-813: a resizable drawer's width, kept per viewer in the settings store
 * (`ui.drawerWidths[id]`, absent = the drawer's default) and synced with the
 * rest of `ui`. Returns `DrawerContent`'s `resize` prop.
 *
 * The widest a drawer may go is the viewport less a gutter, so a strip of the
 * overlay stays to click away; the default is brought inside that too. Below
 * `sm` the drawer is full width whatever is stored (`DrawerContent`).
 */
import type { DrawerResize } from '@/components/ui/Drawer'
import { storedWidth } from '@/components/ui/panelWidth'
import { useSettingsStore } from '@/stores/settingsStore'

/** The overlay strip a drawer at its widest leaves on the left. */
export const DRAWER_GUTTER = 64

export function useDrawerWidth(id: string, limits: { default: number; min: number }, label: string): DrawerResize {
  const stored = useSettingsStore((s) => s.ui.drawerWidths?.[id])
  const setDrawerWidth = useSettingsStore((s) => s.setDrawerWidth)
  const max = Math.max(limits.min, window.innerWidth - DRAWER_GUTTER)
  const bounded = { default: Math.min(limits.default, max), min: limits.min, max }
  return {
    width: storedWidth(stored, bounded),
    min: limits.min,
    max,
    label,
    onCommit: (width) => setDrawerWidth(id, width),
    onReset: () => setDrawerWidth(id, null),
  }
}
